import { useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, X, Search, UserPlus, Repeat, Columns, Headphones, UsersRound, FolderOpen, type LucideIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { useCreateProject } from '../hooks/useProjects';
import { useWorkflows } from '../hooks/useWorkflows';
import { checkProjectKey, searchUsers, type ProjectMemberUser } from '../services/projectService';
import Avatar from '../components/common/Avatar';
import type { ProfileColorKey } from '@maios/shared';
import { getContrastText } from '../components/issue/IssueListView';
import { useAuthStore } from '../store/authStore';
import Spinner from '../components/ui/Spinner';

// --- Zod schema (명세 3.1.2 기반) ---
const schema = z.object({
  name: z.string().min(2, '2자 이상 입력하세요.').max(50, '50자 이하로 입력하세요.'),
  projectKey: z
    .string()
    .min(2, '2자 이상 입력하세요.')
    .max(10, '10자 이하로 입력하세요.')
    .regex(/^[A-Z]+$/, '영문 대문자만 가능합니다.'),
  description: z.string().max(2000, '2,000자 이하로 입력하세요.').optional(),
  projectType: z.enum(['KANBAN', 'SCRUM', 'CS', 'TEAM']),
  visibility: z.enum(['PUBLIC', 'PRIVATE']),
  startDate: z.string().min(1, '시작일을 입력하세요.'),
  endDate: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

export default function ProjectCreatePage() {
  const navigate = useNavigate();
  const createProject = useCreateProject();
  const currentUser = useAuthStore((s) => s.user);
  const { data: workflows } = useWorkflows();
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string>('');
  const selectedWorkflow = workflows?.find((w) => w.workflowId === selectedWorkflowId)
    ?? workflows?.find((w) => w.isDefault);

  // --- Key check ---
  const [keyAvailable, setKeyAvailable] = useState<boolean | null>(null);
  const [keyChecking, setKeyChecking] = useState(false);

  // --- Leader ---
  const [leaderId, setLeaderId] = useState<string>('');

  // --- Members ---
  const [selectedMembers, setSelectedMembers] = useState<ProjectMemberUser[]>([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberResults, setMemberResults] = useState<ProjectMemberUser[]>([]);
  const [searchingMembers, setSearchingMembers] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      projectType: 'KANBAN',
      visibility: 'PRIVATE',
      startDate: new Date().toISOString().split('T')[0],
    },
  });

  const projectName = watch('name');

  // --- Auto-suggest project key ---
  const handleNameBlur = useCallback(() => {
    const currentKey = watch('projectKey');
    if (!currentKey && projectName) {
      const suggested = projectName
        .replace(/[^a-zA-Z\s]/g, '')
        .split(/\s+/)
        .map((w) => w[0]?.toUpperCase() ?? '')
        .join('')
        .slice(0, 6);
      if (suggested.length >= 2) {
        setValue('projectKey', suggested, { shouldValidate: true });
      }
    }
  }, [projectName, watch, setValue]);

  // --- Project key duplicate check ---
  const handleKeyChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.toUpperCase();
      setValue('projectKey', value, { shouldValidate: true });

      if (value.length < 2) {
        setKeyAvailable(null);
        return;
      }

      setKeyChecking(true);
      try {
        const available = await checkProjectKey(value);
        setKeyAvailable(available);
      } catch {
        setKeyAvailable(null);
      } finally {
        setKeyChecking(false);
      }
    },
    [setValue],
  );

  // --- Member search ---
  const handleMemberSearch = useCallback(
    async (query: string) => {
      setMemberSearch(query);
      if (query.length < 2) {
        setMemberResults([]);
        return;
      }
      setSearchingMembers(true);
      try {
        const results = await searchUsers(query);
        const filtered = results.filter(
          (u) =>
            u.id !== currentUser?.id && !selectedMembers.some((m) => m.id === u.id),
        );
        setMemberResults(filtered);
      } catch {
        setMemberResults([]);
      } finally {
        setSearchingMembers(false);
      }
    },
    [currentUser, selectedMembers],
  );

  const addMember = (user: ProjectMemberUser) => {
    setSelectedMembers((prev) => [...prev, user]);
    setMemberSearch('');
    setMemberResults([]);
  };

  const removeMember = (userId: string) => {
    setSelectedMembers((prev) => prev.filter((m) => m.id !== userId));
  };

  // --- Submit ---
  const onSubmit = async (data: FormValues) => {
    if (keyAvailable === false) {
      toast.error('이미 사용 중인 프로젝트 키입니다.');
      return;
    }

    try {
      const result = await createProject.mutateAsync({
        ...data,
        startDate: new Date(data.startDate).toISOString(),
        endDate: data.endDate ? new Date(data.endDate).toISOString() : undefined,
        leaderId: leaderId || undefined,
        memberIds: selectedMembers.length > 0 ? selectedMembers.map((m) => m.id) : undefined,
        workflowId: selectedWorkflow?.workflowId,
      });
      toast.success('프로젝트가 생성되었습니다!');
      navigate(`/projects/${result.projectKey}`, { state: { projectId: result.id } });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '프로젝트 생성에 실패했습니다.';
      toast.error(message);
    }
  };

  // --- Options ---
  const projectTypes: { value: string; label: string; desc: string; icon: LucideIcon }[] = [
    { value: 'SCRUM', label: '스크럼', desc: '스프린트 기반 애자일 프로젝트', icon: Repeat },
    { value: 'KANBAN', label: '칸반', desc: '연속 흐름 기반 프로젝트', icon: Columns },
    { value: 'CS', label: 'CS', desc: '고객 지원/작업 관리 특화 프로젝트', icon: Headphones },
    { value: 'TEAM', label: '팀 작업', desc: '파트/스쿼드/팀 단위의 스프린트 기반 업무 관리', icon: UsersRound },
  ];

  const visibilityOptions = [
    { value: 'PRIVATE', label: 'Private', desc: '멤버만 접근 가능' },
    { value: 'PUBLIC', label: 'Public', desc: '조직 전체 공개' },
  ];

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link
          to="/projects"
          className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <ArrowLeft className="size-5" />
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">새 프로젝트 생성</h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* ─── 기본 정보 ─── */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-5">
          {/* 프로젝트명 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              프로젝트명 <span className="text-red-500">*</span>
            </label>
            <input
              {...register('name')}
              onBlur={handleNameBlur}
              placeholder="예: MaioS 백엔드"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
          </div>

          {/* 프로젝트 키 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              프로젝트 키 <span className="text-red-500">*</span>
              <span className="text-sm text-gray-400 ml-1">(영문 대문자 2~10자)</span>
            </label>
            <div className="relative">
              <input
                value={watch('projectKey') || ''}
                onChange={handleKeyChange}
                placeholder="예: TF"
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none uppercase ${
                  keyAvailable === false
                    ? 'border-red-300 focus:border-red-500'
                    : keyAvailable === true
                      ? 'border-green-300 focus:border-green-500'
                      : 'border-gray-300 focus:border-primary-500'
                }`}
              />
              {keyChecking && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Spinner className="size-4" />
                </div>
              )}
            </div>
            {errors.projectKey && (
              <p className="text-red-500 text-sm mt-1">{errors.projectKey.message}</p>
            )}
            {keyAvailable === false && (
              <p className="text-red-500 text-sm mt-1">이미 사용 중인 프로젝트 키입니다.</p>
            )}
            {keyAvailable === true && (
              <p className="text-green-600 text-sm mt-1">사용 가능한 프로젝트 키입니다.</p>
            )}
            <p className="text-gray-400 text-sm mt-1">작업 번호 접두사로 사용됩니다 (예: TF-1, TF-2)</p>
          </div>

          {/* 설명 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">설명</label>
            <textarea
              {...register('description')}
              rows={3}
              placeholder="프로젝트에 대한 간단한 설명을 입력하세요. (최대 2,000자)"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none"
            />
            {errors.description && (
              <p className="text-red-500 text-sm mt-1">{errors.description.message}</p>
            )}
          </div>

          {/* 프로젝트 유형 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              프로젝트 유형 <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {projectTypes.map((type) => (
                <label
                  key={type.value}
                  className={`relative flex flex-col items-center p-4 border rounded-lg cursor-pointer transition-all ${
                    watch('projectType') === type.value
                      ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    {...register('projectType')}
                    value={type.value}
                    className="sr-only"
                  />
                  <type.icon className="size-6 text-gray-500 mb-1" />
                  <span className="text-sm font-medium text-gray-900">{type.label}</span>
                  <span className="text-sm text-gray-500 mt-1 text-center">{type.desc}</span>
                </label>
              ))}
            </div>
          </div>

          {/* 공개 설정 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              공개 설정 <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              {visibilityOptions.map((opt) => (
                <label
                  key={opt.value}
                  className={`relative flex flex-col items-center p-4 border rounded-lg cursor-pointer transition-all ${
                    watch('visibility') === opt.value
                      ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    {...register('visibility')}
                    value={opt.value}
                    className="sr-only"
                  />
                  <span className="text-sm font-medium text-gray-900">{opt.label}</span>
                  <span className="text-sm text-gray-500 mt-1 text-center">{opt.desc}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* ─── 워크플로우 (CHG-041 REQ-V41-014) ─── */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">워크플로우</label>
            <select
              value={selectedWorkflowId || selectedWorkflow?.workflowId || ''}
              onChange={(e) => setSelectedWorkflowId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
            >
              {workflows?.map((w) => (
                <option key={w.workflowId} value={w.workflowId}>
                  {w.name}{w.isDefault ? ' (기본)' : ''}
                </option>
              ))}
            </select>
          </div>
          {selectedWorkflow && (
            <div className="flex items-center gap-1 flex-wrap">
              {selectedWorkflow.statuses.map((status, i) => (
                <span key={status.statusId} className="flex items-center gap-1">
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-sm font-medium rounded-full"
                    style={{ backgroundColor: status.color, color: getContrastText(status.color) }}
                  >
                    {status.name}
                  </span>
                  {i < selectedWorkflow.statuses.length - 1 && (
                    <span className="text-gray-300 text-sm">→</span>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* ─── 일정 ─── */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                시작일 <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                {...register('startDate')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              />
              {errors.startDate && (
                <p className="text-red-500 text-sm mt-1">{errors.startDate.message}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">종료 예정일</label>
              <input
                type="date"
                {...register('endDate')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              />
            </div>
          </div>
        </div>

        {/* ─── 팀 구성 ─── */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-5">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">팀 구성</h2>

          {/* 프로젝트 리더 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              프로젝트 리더 <span className="text-red-500">*</span>
            </label>
            <select
              value={leaderId}
              onChange={(e) => setLeaderId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
            >
              <option value="">나 ({currentUser?.name})</option>
              {selectedMembers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} ({m.email})
                </option>
              ))}
            </select>
            <p className="text-gray-400 text-sm mt-1">비워두면 본인이 리더로 지정됩니다.</p>
          </div>

          {/* 초기 멤버 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">초기 멤버</label>
            <div className="relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
                <input
                  value={memberSearch}
                  onChange={(e) => handleMemberSearch(e.target.value)}
                  placeholder="사용자 검색하여 추가..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                />
                {searchingMembers && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Spinner className="size-4" />
                  </div>
                )}
              </div>

              {/* Search results dropdown */}
              {memberResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {memberResults.map((user) => (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => addMember(user)}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 transition-colors text-left"
                    >
                      <Avatar
                        size="sm"
                        userId={user.id}
                        name={user.name}
                        avatarUrl={user.avatarUrl}
                        displayColor={user.displayColor as ProfileColorKey | null | undefined}
                        firstName={user.firstName}
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{user.name}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                      <UserPlus className="size-4 text-gray-400 ml-auto" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Selected members tags */}
            {selectedMembers.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {selectedMembers.map((m) => (
                  <span
                    key={m.id}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary-50 text-primary-700 rounded-full text-sm font-medium"
                  >
                    {m.name}
                    <button
                      type="button"
                      onClick={() => removeMember(m.id)}
                      className="hover:text-red-500 transition-colors"
                    >
                      <X className="size-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* CHG-130 REQ-DHR-029: 프로젝트 문서함 자동 생성 안내 (마지막 단계) */}
        <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <div className="size-9 rounded-lg bg-white border border-blue-200 flex items-center justify-center shrink-0">
            <FolderOpen className="size-5 text-blue-600" />
          </div>
          <div className="flex-1 text-sm">
            <p className="font-semibold text-blue-900">프로젝트 문서함이 자동으로 생성됩니다</p>
            <p className="text-blue-700 mt-1 leading-relaxed">
              생성 직후 프로젝트 상단탭의 <span className="font-medium">"문서함"</span>에서 자동으로 만들어진
              <span className="font-medium"> 회의록</span> 폴더(데일리 스크럼 / 일반 회의록)와 함께 바로 사용할 수 있어요.
              프로젝트 멤버는 문서함 멤버로 자동 동기화됩니다.
            </p>
          </div>
        </div>

        {/* ─── 하단 버튼 ─── */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <Link
            to="/projects"
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            취소
          </Link>
          <button
            type="submit"
            disabled={isSubmitting || createProject.isPending}
            className="inline-flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium
              hover:bg-primary-700 disabled:opacity-50 transition-colors shadow-sm"
          >
            {(isSubmitting || createProject.isPending) && <Spinner className="size-4 text-white" />}
            프로젝트 생성
          </button>
        </div>
      </form>
    </div>
  );
}
